/**
 * Client-side dead-reckoning for live aircraft markers (R.1b §3.3). Between
 * server position updates we advance each aircraft along a straight line at its
 * last reported ground speed + heading, so a slow polling cadence still reads
 * as smooth live motion. When a fresh truth arrives the marker snaps to it and
 * the trajectory is recomputed.
 *
 * Straight-line assumption: accurate over short windows; it breaks down through
 * sharp turns, which is why callers cap the extrapolation window (see
 * MAX_INTERP_SECONDS in MapView) rather than projecting indefinitely.
 */

/** Knots → metres per second. */
export const KT_TO_MS = 0.514444;
const M_PER_DEG_LAT = 111_320;

export interface LatLon {
  lat: number;
  lon: number;
}

/**
 * Project a position forward `dtSeconds` along `headingDeg` (0 = north, 90 =
 * east) at `speedKt`. Returns the input unchanged for non-positive/non-finite
 * dt or speed, or at the poles (where longitude is undefined).
 */
export function interpolatePosition(
  lat: number,
  lon: number,
  headingDeg: number | null,
  speedKt: number | null,
  dtSeconds: number,
): LatLon {
  if (
    speedKt === null ||
    headingDeg === null ||
    !Number.isFinite(speedKt) ||
    !Number.isFinite(headingDeg) ||
    !Number.isFinite(dtSeconds) ||
    speedKt <= 0 ||
    dtSeconds <= 0
  ) {
    return { lat, lon };
  }
  const distanceM = speedKt * KT_TO_MS * dtSeconds;
  const hdg = (headingDeg * Math.PI) / 180;
  const dLat = (distanceM * Math.cos(hdg)) / M_PER_DEG_LAT;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const dLon = Math.abs(cosLat) < 1e-6 ? 0 : (distanceM * Math.sin(hdg)) / (M_PER_DEG_LAT * cosLat);
  return { lat: lat + dLat, lon: lon + dLon };
}

// ── Motion-sample planning (D2 §3.3, 3D wave) ────────────────────────────────
// Cesium's SampledPositionProperty interpolates a marker between time-tagged
// truth samples along the viewer clock, so motion is smooth at framerate even
// on a slow (30 s) poll. The old per-frame CallbackPositionProperty re-anchored
// to each new truth, which TELEPORTED the marker by the dead-reckon error every
// poll. Here we instead lay down a short sample plan per fix that (a) pins the
// marker where it is *now* (continuity — no teleport), (b) glides it to the new
// truth over a short blend, then (c) projects it forward along heading+speed so
// it keeps moving between polls. Cesium HOLDs past the last sample, capping
// runaway drift if the feed goes quiet.

/** Glide from the current displayed position onto a fresh truth over this many
 *  seconds, so a late/corrected fix reconciles smoothly instead of snapping. */
export const RECONCILE_BLEND_SECONDS = 2;
/** Project the track this far past a truth (constant heading+speed). Matches the
 *  old dead-reckon cap: comfortably longer than the poll cadence so normal polls
 *  stay inside the interpolation window, but bounded so a dead feed glides to a
 *  stop rather than drifting forever. */
export const DEADRECKON_HORIZON_SECONDS = 120;

export interface MotionSample extends LatLon {
  /** Seconds from "now" (the moment the fix was applied). */
  dt: number;
  /** Metres above the ellipsoid. */
  alt: number;
}

export interface PositionAtTime extends LatLon {
  alt: number;
}

/**
 * Plan the SampledPositionProperty samples to lay down when a fresh truth lands.
 *
 * `displayed` is where the marker currently appears (the interpolated/projected
 * position at "now"); pass null for the very first fix. The plan is returned as
 * dt-relative samples so the caller stamps them against a single `JulianDate.now()`.
 */
export function planMotionSamples(
  displayed: PositionAtTime | null,
  truth: PositionAtTime,
  headingDeg: number | null,
  speedKt: number | null,
  opts: { blendSeconds?: number; horizonSeconds?: number } = {},
): MotionSample[] {
  const blend = opts.blendSeconds ?? RECONCILE_BLEND_SECONDS;
  const horizon = opts.horizonSeconds ?? DEADRECKON_HORIZON_SECONDS;
  const projected = interpolatePosition(truth.lat, truth.lon, headingDeg, speedKt, horizon);
  const samples: MotionSample[] = [];
  if (displayed) {
    // Pin current position → glide to truth → project forward.
    samples.push({ dt: 0, lat: displayed.lat, lon: displayed.lon, alt: displayed.alt });
    samples.push({ dt: blend, lat: truth.lat, lon: truth.lon, alt: truth.alt });
    samples.push({ dt: blend + horizon, lat: projected.lat, lon: projected.lon, alt: truth.alt });
  } else {
    // First sighting: drop the marker on truth and project forward.
    samples.push({ dt: 0, lat: truth.lat, lon: truth.lon, alt: truth.alt });
    samples.push({ dt: horizon, lat: projected.lat, lon: projected.lon, alt: truth.alt });
  }
  return samples;
}
